// Routenberechnung für den Missionsplaner (Co-Pilot-Modus).
// Rechnet in einem lokalen Meter-Raster um den Mittelpunkt der Eingabe (für Einsatzgebiete von wenigen km genau
// genug) und gibt Wegpunkte als [lat, lon] zurück. Die Pilot-API schreibt daraus die DJI-Routendatei (KMZ).

export type LatLon = [number, number]
type XY = [number, number]

export type RoutenTyp = 'WEGPUNKTE' | 'FLAECHE' | 'LINIE' | 'SUCHMUSTER'
export type Muster = 'PARALLEL' | 'EXPANDING_SQUARE' | 'SEKTOR' | 'UFERLINIE'
export type Linse = 'wide' | 'zoom' | 'ir'
export type Seite = 'beide' | 'links' | 'rechts'

export interface RoutenParameter {
  hoehe_m: number
  tempo_ms: number
  linsen: Linse[]
  gimbal_pitch: number
  /** Sensor, aus dessen Bildbreite der Spurabstand berechnet wird */
  abstand_sensor: 'wide' | 'ir'
  /** Seitliche Überlappung der Bildstreifen in % (Suche ~20, Mapping ~60) */
  ueberlappung_seite: number
  /** Überlappung in Flugrichtung in % (nur Fläche/Mapping) */
  ueberlappung_front: number
  /** Spurabstand in m; null = aus Höhe, Bildwinkel und Überlappung */
  abstand_m: number | null
  /** Flugrichtung der Bahnen in Grad (0 = Nord); null = längste Kante */
  richtung_grad: number | null
  /** Expanding Square / Sektor: Suchradius in m */
  radius_m: number
  /** Linie / Uferlinie: Anzahl Bahnen und Seite */
  bahnen: number
  seite: Seite
  /** Nutzbare Flugzeit je Akku in Minuten (Reserve abgezogen) */
  akku_min: number
  geraet: string
}

export const STANDARD_PARAMETER: RoutenParameter = {
  hoehe_m: 60, tempo_ms: 7, linsen: ['ir', 'wide'], gimbal_pitch: -90, abstand_sensor: 'ir',
  ueberlappung_seite: 20, ueberlappung_front: 70, abstand_m: null, richtung_grad: null,
  radius_m: 300, bahnen: 2, seite: 'beide', akku_min: 30, geraet: 'M4TD',
}

/** Horizontaler Bildwinkel (Grad) und Seitenverhältnis Breite/Höhe der Kameras der Matrice 4TD (Näherung aus
 *  den Herstellerangaben). Für den Spurabstand reicht diese Genauigkeit; er lässt sich von Hand überschreiben. */
export const KAMERAS: Record<'wide' | 'ir', { hfov: number; seitenverhaeltnis: number; name: string }> = {
  wide: { hfov: 69.6, seitenverhaeltnis: 4 / 3, name: 'Weitwinkel' },
  ir: { hfov: 35.9, seitenverhaeltnis: 5 / 4, name: 'Wärmebild' },
}

const rad = (g: number) => (g * Math.PI) / 180

/** Breite und Länge des Bildausschnitts am Boden (m) bei Nadir-Blick. */
export function bodenausschnitt(hoehe: number, sensor: 'wide' | 'ir') {
  const k = KAMERAS[sensor]
  const breite = 2 * hoehe * Math.tan(rad(k.hfov / 2))
  return { breite, laenge: breite / k.seitenverhaeltnis }
}

export function spurabstand(p: RoutenParameter): number {
  if (p.abstand_m && p.abstand_m > 0) return p.abstand_m
  const { breite } = bodenausschnitt(p.hoehe_m, p.abstand_sensor)
  return Math.max(5, breite * (1 - p.ueberlappung_seite / 100))
}

/* ------------------------------------------------------------- Projektion */

interface Projektion { nach: (p: LatLon) => XY; zurueck: (p: XY) => LatLon }

function projektion(ursprung: LatLon): Projektion {
  const kx = 111320 * Math.cos(rad(ursprung[0]))
  const ky = 110540
  return {
    nach: ([lat, lon]) => [(lon - ursprung[1]) * kx, (lat - ursprung[0]) * ky],
    zurueck: ([x, y]) => [ursprung[0] + y / ky, ursprung[1] + x / kx],
  }
}

const mitte = (punkte: LatLon[]): LatLon =>
  [punkte.reduce((s, p) => s + p[0], 0) / punkte.length, punkte.reduce((s, p) => s + p[1], 0) / punkte.length]

export function abstandM(a: LatLon, b: LatLon): number {
  const pr = projektion(a)
  const [x, y] = pr.nach(b)
  return Math.hypot(x, y)
}

/** Punkt in Richtung (Grad, 0 = Nord, im Uhrzeigersinn) und Entfernung (m) */
const versatz = ([x, y]: XY, richtung: number, m: number): XY => [x + Math.sin(rad(richtung)) * m, y + Math.cos(rad(richtung)) * m]

const drehen = ([x, y]: XY, winkel: number): XY => {
  const c = Math.cos(winkel), s = Math.sin(winkel)
  return [x * c - y * s, x * s + y * c]
}

/* ------------------------------------------------------------- Muster */

/** Mäander (Parallelsuche/Mapping) über ein Polygon: Bahnen im Abstand `abstand` in Richtung `richtung`. */
function maeander(poly: XY[], abstand: number, richtung: number): XY[] {
  // Polygon so drehen, dass die Bahnen waagrecht (entlang x) laufen
  const w = rad(90 - richtung)
  const gedreht = poly.map(p => drehen(p, -w))
  const ys = gedreht.map(p => p[1])
  const minY = Math.min(...ys), maxY = Math.max(...ys)
  const bahnen: XY[][] = []
  for (let y = minY + abstand / 2; y < maxY; y += abstand) {
    const xs: number[] = []
    for (let i = 0; i < gedreht.length; i++) {
      const a = gedreht[i], b = gedreht[(i + 1) % gedreht.length]
      if ((a[1] <= y && b[1] > y) || (b[1] <= y && a[1] > y)) xs.push(a[0] + ((y - a[1]) * (b[0] - a[0])) / (b[1] - a[1]))
    }
    xs.sort((a, b) => a - b)
    for (let i = 0; i + 1 < xs.length; i += 2) {
      // bei konkaven Flächen mehrere Abschnitte je Bahn
      const teil: XY[] = [[xs[i], y], [xs[i + 1], y]]
      bahnen.push(teil)
    }
  }
  const pfad: XY[] = []
  bahnen.forEach((b, i) => pfad.push(...(i % 2 === 0 ? b : [b[1], b[0]])))
  return pfad.map(p => drehen(p, w))
}

/** Richtung (Grad) der längsten Polygonkante – Bahnen parallel dazu ergeben die wenigsten Wenden. */
function laengsteKante(poly: XY[]): number {
  let best = 0, richtung = 0
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length]
    const l = Math.hypot(b[0] - a[0], b[1] - a[1])
    if (l > best) { best = l; richtung = (Math.atan2(b[0] - a[0], b[1] - a[1]) * 180) / Math.PI }
  }
  return (richtung + 360) % 360
}

/** Expanding Square (IAMSAR): Schenkel s, s, 2s, 2s, 3s … mit 90°-Wenden, bis der Radius abgedeckt ist. */
function expandingSquare(radius: number, abstand: number, richtung: number): XY[] {
  const pfad: XY[] = [[0, 0]]
  let p: XY = [0, 0]
  let r = richtung
  for (let i = 1; i < 400; i++) {
    const laenge = Math.ceil(i / 2) * abstand
    if (laenge / 2 > radius + abstand) break
    p = versatz(p, r, laenge)
    pfad.push(p)
    r = (r + 90) % 360
  }
  return pfad
}

/** Sektorsuche (IAMSAR, 3 Dreiecke): Bezugspunkt → Radius → 60° weiter → zurück, um 120° versetzt. */
function sektor(radius: number, richtung: number): XY[] {
  const pfad: XY[] = [[0, 0]]
  for (let k = 0; k < 3; k++) {
    const t = richtung + k * 120
    pfad.push(versatz([0, 0], t, radius), versatz([0, 0], t + 60, radius), [0, 0])
  }
  return pfad
}

/** Parallele Bahnen entlang einer Linie (Fluss, Straße, Ufer), abwechselnd hin und zurück. */
function linienBahnen(linie: XY[], bahnen: number, abstand: number, seite: Seite): XY[] {
  if (linie.length < 2) return linie
  const versaetze = Array.from({ length: Math.max(1, bahnen) }, (_, i) =>
    seite === 'beide' ? (i - (bahnen - 1) / 2) * abstand : (seite === 'links' ? 1 : -1) * (i + 0.5) * abstand)
  const normale = (i: number): XY => {
    // gemittelte Normale (links der Laufrichtung) am Stützpunkt i
    const seg = (a: XY, b: XY): XY => { const l = Math.hypot(b[0] - a[0], b[1] - a[1]) || 1; return [-(b[1] - a[1]) / l, (b[0] - a[0]) / l] }
    const n1 = i > 0 ? seg(linie[i - 1], linie[i]) : seg(linie[0], linie[1])
    const n2 = i < linie.length - 1 ? seg(linie[i], linie[i + 1]) : n1
    const nx = n1[0] + n2[0], ny = n1[1] + n2[1]
    const l = Math.hypot(nx, ny) || 1
    const cos = (nx / l) * n1[0] + (ny / l) * n1[1]
    const f = 1 / Math.max(0.3, cos)   // Gehrung, begrenzt
    return [(nx / l) * f, (ny / l) * f]
  }
  const pfad: XY[] = []
  versaetze.forEach((d, k) => {
    const bahn = linie.map((p, i) => { const n = normale(i); return [p[0] + n[0] * d, p[1] + n[1] * d] as XY })
    pfad.push(...(k % 2 === 0 ? bahn : bahn.reverse()))
  })
  return pfad
}

/* ------------------------------------------------------------- Route */

export interface Geometrie {
  /** Eckpunkte (Fläche/Parallel), Stützpunkte (Linie/Uferlinie) oder Wegpunkte */
  punkte: LatLon[]
  /** Bezugspunkt (Expanding Square, Sektor) */
  mittelpunkt: LatLon | null
  /** Startplatz der Drohne (für Anflug, Entfernung und Flugzeit) */
  startplatz: LatLon | null
}

export interface Kennzahlen {
  strecke_m: number
  flugzeit_min: number
  akkus: number
  flaeche_ha: number | null
  fotos: number | null
  abstand_m: number
  max_entfernung_m: number
  wegpunkte: number
}

/** Welche Eingabe der Routentyp braucht */
export function eingabeArt(typ: RoutenTyp, muster: Muster | null): 'flaeche' | 'linie' | 'punkte' | 'mittelpunkt' {
  if (typ === 'FLAECHE' || (typ === 'SUCHMUSTER' && muster === 'PARALLEL')) return 'flaeche'
  if (typ === 'LINIE' || (typ === 'SUCHMUSTER' && muster === 'UFERLINIE')) return 'linie'
  if (typ === 'SUCHMUSTER' && (muster === 'EXPANDING_SQUARE' || muster === 'SEKTOR')) return 'mittelpunkt'
  return 'punkte'
}

/** Aktion entlang der Route für die KMZ: Fläche = Fotos im Abstand, Suche = Video */
export function routenAktion(typ: RoutenTyp): 'fotos' | 'video' | 'keine' {
  if (typ === 'FLAECHE') return 'fotos'
  if (typ === 'SUCHMUSTER' || typ === 'LINIE') return 'video'
  return 'keine'
}

export function fotoAbstand(p: RoutenParameter): number {
  return Math.max(2, bodenausschnitt(p.hoehe_m, 'wide').laenge * (1 - p.ueberlappung_front / 100))
}

function polygonFlaeche(poly: XY[]): number {
  let s = 0
  for (let i = 0; i < poly.length; i++) { const a = poly[i], b = poly[(i + 1) % poly.length]; s += a[0] * b[1] - b[0] * a[1] }
  return Math.abs(s) / 2
}

export function berechneRoute(typ: RoutenTyp, muster: Muster | null, g: Geometrie, p: RoutenParameter):
  { wegpunkte: LatLon[]; kennzahlen: Kennzahlen | null } {
  const art = eingabeArt(typ, muster)
  const abstand = typ === 'FLAECHE'
    ? (p.abstand_m && p.abstand_m > 0 ? p.abstand_m : Math.max(3, bodenausschnitt(p.hoehe_m, 'wide').breite * (1 - p.ueberlappung_seite / 100)))
    : spurabstand(p)
  const basis = art === 'mittelpunkt' ? (g.mittelpunkt ? [g.mittelpunkt] : []) : g.punkte
  if (!basis.length) return { wegpunkte: [], kennzahlen: null }
  const pr = projektion(mitte(basis))

  let pfad: XY[] = []
  let flaeche: number | null = null
  if (art === 'flaeche') {
    if (g.punkte.length < 3) return { wegpunkte: [], kennzahlen: null }
    const poly = g.punkte.map(pr.nach)
    pfad = maeander(poly, abstand, p.richtung_grad ?? laengsteKante(poly))
    flaeche = polygonFlaeche(poly)
  } else if (art === 'linie') {
    if (g.punkte.length < 2) return { wegpunkte: [], kennzahlen: null }
    pfad = linienBahnen(g.punkte.map(pr.nach), p.bahnen, abstand, p.seite)
  } else if (art === 'mittelpunkt') {
    const c = pr.nach(g.mittelpunkt!)
    const roh = muster === 'SEKTOR' ? sektor(p.radius_m, p.richtung_grad ?? 0) : expandingSquare(p.radius_m, abstand, p.richtung_grad ?? 0)
    pfad = roh.map(([x, y]) => [x + c[0], y + c[1]] as XY)
    flaeche = muster === 'SEKTOR' ? Math.PI * p.radius_m ** 2 : (2 * p.radius_m) ** 2
  } else {
    pfad = g.punkte.map(pr.nach)
  }
  // doppelte Punkte (z. B. Rückkehr zum Bezugspunkt direkt hintereinander) entfernen
  pfad = pfad.filter((q, i) => i === 0 || Math.hypot(q[0] - pfad[i - 1][0], q[1] - pfad[i - 1][1]) > 0.5)
  const wegpunkte = pfad.map(pr.zurueck)
  if (wegpunkte.length < 2) return { wegpunkte, kennzahlen: null }

  let strecke = 0
  for (let i = 1; i < pfad.length; i++) strecke += Math.hypot(pfad[i][0] - pfad[i - 1][0], pfad[i][1] - pfad[i - 1][1])
  let anflug = 0, maxEntf = 0
  if (g.startplatz) {
    const s = pr.nach(g.startplatz)
    const d = (q: XY) => Math.hypot(q[0] - s[0], q[1] - s[1])
    anflug = d(pfad[0]) + d(pfad[pfad.length - 1])
    maxEntf = Math.max(...pfad.map(d))
  }
  // Flugzeit: Strecke mit Reisetempo, An-/Rückflug mit 10 m/s, 3 s je Wende, Steigen/Sinken mit 5 m/s
  const sekunden = strecke / Math.min(15, Math.max(1, p.tempo_ms)) + anflug / 10 + Math.max(0, pfad.length - 2) * 3 + (2 * p.hoehe_m) / 5
  const flugzeit = sekunden / 60
  return {
    wegpunkte,
    kennzahlen: {
      strecke_m: Math.round(strecke + anflug),
      flugzeit_min: Math.round(flugzeit * 10) / 10,
      akkus: Math.max(1, Math.ceil(flugzeit / Math.max(5, p.akku_min))),
      flaeche_ha: flaeche !== null ? Math.round(flaeche / 100) / 100 : null,
      fotos: typ === 'FLAECHE' ? Math.ceil(strecke / fotoAbstand(p)) : null,
      abstand_m: Math.round(abstand * 10) / 10,
      max_entfernung_m: Math.round(maxEntf),
      wegpunkte: wegpunkte.length,
    },
  }
}
